import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, Row, Col, Table, Button, Modal, Form, Badge, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import Icon from '../components/Icon.jsx';
import { Edit3, Trash2, Trophy, Users, UserMinus, Wallet } from 'lucide-react';
import { useGetTeamByIdQuery, useGetTeamsByLeagueQuery, useCreateTeamMutation, useUpdateTeamMutation, useDeleteTeamMutation } from '../slices/teamApiSlice.js';
import { useFetchAvailableLeaguesQuery } from '../slices/leaguesApiSlice.js';
import { useFetchAthleteByUserIdQuery, useFetchAthletesByDraftedTeamIdQuery } from '../slices/athleteApiSlice.js';
import { useGetDraftByLeagueQuery } from '../slices/draftApiSlice.js';
import { useGetCurrentSeasonQuery } from '../slices/seasonsApiSlice.js';
import { buildDraftRoomPath } from '../utils/draftRoomMode';
import { setStoredActiveLeagueId, useActiveLeagueId } from '../utils/activeLeague';
import './TeamScreen.css';

const ACTIVE_SCORERS_PER_TEAM = 5;

const getLeagueName = (league, fallback = 'Not in a league') => {
  if (!league) return fallback;
  if (typeof league === 'string') return league;
  return league.name || fallback;
};

const normalizeWeeklyBenchSelections = (weeklyBenchSelections) => {
  if (!weeklyBenchSelections) return {};
  if (weeklyBenchSelections instanceof Map) {
    return Object.fromEntries(weeklyBenchSelections);
  }
  if (Array.isArray(weeklyBenchSelections)) {
    return Object.fromEntries(weeklyBenchSelections);
  }
  return weeklyBenchSelections;
};

const normalizeBenchAthleteIds = (value) => {
  if (!value) return [];

  const values = Array.isArray(value) ? value : [value];
  const seenIds = new Set();

  return values.reduce((ids, entry) => {
    const normalizedId = String(entry?._id || entry || '').trim();
    if (!normalizedId || seenIds.has(normalizedId)) {
      return ids;
    }

    seenIds.add(normalizedId);
    ids.push(normalizedId);
    return ids;
  }, []);
};

const getBenchSlotsForRosterSize = (rosterSize) => Math.max(0, Number(rosterSize || 0) - ACTIVE_SCORERS_PER_TEAM);

const getCurrentWeekNumber = (season) => {
  if (!season?.startDate) return null;

  const startDate = new Date(season.startDate);
  const now = new Date();

  if (Number.isNaN(startDate.getTime())) return null;
  if (now < startDate) return null;

  const diffMs = now.getTime() - startDate.getTime();
  return Math.floor(diffMs / (1000 * 60 * 60 * 24 * 7)) + 1;
};

const getConfigurableBenchWeekNumber = (season) => {
  if (!season?.startDate) return null;

  const startDate = new Date(season.startDate);
  const now = new Date();

  if (Number.isNaN(startDate.getTime())) return null;
  if (now < startDate) return 1;

  const currentWeekNumber = getCurrentWeekNumber(season);
  return currentWeekNumber == null ? null : currentWeekNumber + 1;
};

const getAthleteDisplayName = (runner) => {
  if (!runner) return 'Unnamed athlete';

  const fullName = [runner.firstName, runner.lastName].filter(Boolean).join(' ').trim();
  if (fullName) return fullName;

  if (runner.user && typeof runner.user === 'object') {
    if (runner.user.username) return runner.user.username;
    if (runner.user.email) return runner.user.email;
  }

  return 'Unnamed athlete';
};

const getDisplayTeamName = (name) => {
  const normalizedName = String(name || '').trim();
  if (!normalizedName) return 'Unnamed team';

  const ownerStyleName = normalizedName.replace(/\s*'?s\s+team$/i, '').trim();
  if (ownerStyleName !== normalizedName) {
    return ownerStyleName;
  }

  const genericSuffixName = normalizedName.replace(/\s+team$/i, '').trim();
  return genericSuffixName || normalizedName;
};

const getResolvedEntityId = (value) => {
  if (!value) return '';
  if (typeof value === 'object') {
    return value._id || value.id || value.teamId || '';
  }
  return value;
};

const getResolvedAdminId = (value) => {
  if (!value) return '';

  const rawId = value?.admin?._id || value?.admin;
  if (!rawId) return '';

  return typeof rawId === 'object' ? rawId._id || rawId.id || '' : rawId;
};

const getRunnerSeasonPoints = (runner) => runner?.seasonTotalPoints ?? runner?.totalPoints ?? runner?.points ?? 0;

const isRunnerBenched = (runner, benchAthleteIds = []) => normalizeBenchAthleteIds(benchAthleteIds).includes(String(runner?._id || ''));

const getDefaultBenchAthleteIds = ({ team, draftedAthletes }) => {
  const explicitBenchAthleteIds = normalizeBenchAthleteIds(team?.benchAthletes);
  if (explicitBenchAthleteIds.length > 0) {
    return explicitBenchAthleteIds;
  }

  const legacyBenchAthleteIds = normalizeBenchAthleteIds(team?.benchAthlete);
  if (legacyBenchAthleteIds.length > 0) {
    return legacyBenchAthleteIds;
  }

  const benchSlots = getBenchSlotsForRosterSize(draftedAthletes.length);
  if (benchSlots <= 0) {
    return [];
  }

  return draftedAthletes.slice(-benchSlots).map((runner) => String(runner._id));
};

const getBenchAthleteIdsForWeek = ({ team, draftedAthletes, weekNumber }) => {
  const weeklyBenchSelections = normalizeWeeklyBenchSelections(team?.weeklyBenchSelections);
  const weeklySelection = weekNumber ? normalizeBenchAthleteIds(weeklyBenchSelections[String(weekNumber)]) : [];

  if (weeklySelection.length > 0) {
    return weeklySelection;
  }

  return getDefaultBenchAthleteIds({ team, draftedAthletes });
};

const TeamScreen = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { userInfo } = useSelector((state) => state.auth);
  const storedActiveLeagueId = useActiveLeagueId();
  const requestedLeagueId = searchParams.get('leagueId') || storedActiveLeagueId || '';
  const isCreateTeamLockedToLeague = Boolean(requestedLeagueId && !id);
  const athleteRequest = requestedLeagueId
    ? { userId: userInfo?._id, leagueId: requestedLeagueId }
    : userInfo?._id;
  const { data: athlete } = useFetchAthleteByUserIdQuery(athleteRequest, {
    skip: !userInfo,
  });
  const athleteTeam = athlete?.team && typeof athlete.team === 'object' ? athlete.team : null;
  const resolvedTeamId = id || getResolvedEntityId(athlete?.team);
  const scopedLeagueId = requestedLeagueId || getResolvedEntityId(athlete?.league);
  const {
    data: specificTeam,
    isLoading: isLoadingSpecificTeam,
    error: specificTeamError,
    refetch: refetchTeam,
  } = useGetTeamByIdQuery(resolvedTeamId, {
    skip: !resolvedTeamId,
  });
  const { data: leagueTeams = [], isLoading: isLoadingLeagueTeams } = useGetTeamsByLeagueQuery(scopedLeagueId, {
    skip: !scopedLeagueId || Boolean(id),
  });

  const fallbackOwnedLeagueTeam = useMemo(() => {
    if (!Array.isArray(leagueTeams) || !leagueTeams.length) {
      return null;
    }

    const activeTeamId = getResolvedEntityId(athlete?.team);
    if (activeTeamId) {
      const matchingActiveTeam = leagueTeams.find((team) => String(getResolvedEntityId(team)) === String(activeTeamId));
      if (matchingActiveTeam) {
        return matchingActiveTeam;
      }
    }

    const ownedTeam = leagueTeams.find((team) => String(getResolvedAdminId(team)) === String(userInfo?._id || ''));
    if (ownedTeam) {
      return ownedTeam;
    }

    const athleteId = String(athlete?._id || '');
    if (!athleteId) {
      return null;
    }

    return leagueTeams.find((team) => Array.isArray(team?.athlete) && team.athlete.some((entry) => String(entry?._id || entry) === athleteId)) || null;
  }, [athlete?._id, athlete?.team, leagueTeams, userInfo?._id]);

  const teamToDisplay = id ? specificTeam : (specificTeam || athleteTeam || fallbackOwnedLeagueTeam);
  const teamId = id || getResolvedEntityId(specificTeam) || getResolvedEntityId(athlete?.team) || getResolvedEntityId(fallbackOwnedLeagueTeam);
  const { data: draftedAthletes = [], isLoading: isLoadingDraftedAthletes } = useFetchAthletesByDraftedTeamIdQuery(
    teamId,
    { skip: !teamId }
  );
  const { data: leagues = [] } = useFetchAvailableLeaguesQuery(userInfo?._id, {
    skip: !userInfo?._id,
  });
  const activeLeagueId = getResolvedEntityId(teamToDisplay?.league);
  const { data: currentDraft } = useGetDraftByLeagueQuery(activeLeagueId, {
    skip: !activeLeagueId,
  });
  const { data: currentSeason } = useGetCurrentSeasonQuery(activeLeagueId, {
    skip: !activeLeagueId,
  });

  const [createTeam] = useCreateTeamMutation();
  const [updateTeam] = useUpdateTeamMutation();
  const [deleteTeam] = useDeleteTeamMutation();
  const [teamName, setTeamName] = useState('');
  const [teamDescription, setTeamDescription] = useState('');
  const [selectedLeague, setSelectedLeague] = useState('');
  const [selectedBenchAthletes, setSelectedBenchAthletes] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isSavingBench, setIsSavingBench] = useState(false);

  useEffect(() => {
    if (teamToDisplay) {
      setTeamName(teamToDisplay.name || '');
      setTeamDescription(teamToDisplay.description || '');
      setSelectedLeague(getResolvedEntityId(teamToDisplay.league));
      return;
    }

    if (requestedLeagueId) {
      setSelectedLeague(requestedLeagueId);
    }
  }, [requestedLeagueId, teamToDisplay]);

  useEffect(() => {
    const nextActiveLeagueId = getResolvedEntityId(teamToDisplay?.league) || requestedLeagueId;
    if (nextActiveLeagueId) {
      setStoredActiveLeagueId(nextActiveLeagueId);
    }
  }, [requestedLeagueId, teamToDisplay]);

  const currentWeekNumber = getCurrentWeekNumber(currentSeason?.season);
  const configurableBenchWeekNumber = getConfigurableBenchWeekNumber(currentSeason?.season);
  const weeklyBenchSelections = normalizeWeeklyBenchSelections(teamToDisplay?.weeklyBenchSelections);
  const currentBenchAthleteIds = useMemo(() => getBenchAthleteIdsForWeek({
    team: teamToDisplay,
    draftedAthletes,
    weekNumber: currentWeekNumber,
  }), [currentWeekNumber, draftedAthletes, teamToDisplay]);
  const editableBenchAthleteIds = useMemo(() => getBenchAthleteIdsForWeek({
    team: teamToDisplay,
    draftedAthletes,
    weekNumber: configurableBenchWeekNumber,
  }), [configurableBenchWeekNumber, draftedAthletes, teamToDisplay]);

  useEffect(() => {
    setSelectedBenchAthletes(editableBenchAthleteIds);
  }, [editableBenchAthleteIds]);

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    if (!selectedLeague) {
      toast.error('Please select a league.');
      return;
    }

    try {
      await createTeam({ name: teamName, description: teamDescription, league: selectedLeague }).unwrap();
      toast.success('Team created successfully.');
      navigate(selectedLeague ? `/leagues/${selectedLeague}` : '/leagues');
    } catch (error) {
      console.error('Failed to create team', error);
      toast.error(error?.data?.message || 'Failed to create team');
    }
  };

  const handleUpdateTeam = async (e) => {
    e.preventDefault();
    if (!teamToDisplay) return;

    try {
      await updateTeam({
        id: teamToDisplay._id,
        name: teamName,
        description: teamDescription,
        league: selectedLeague || teamToDisplay.league,
      }).unwrap();
      setShowEditModal(false);
      toast.success('Team updated successfully.');
    } catch (error) {
      console.error('Failed to update team', error);
      toast.error('Failed to update team');
    }
  };

  const handleDeleteTeam = async (teamIdToDelete) => {
    if (!window.confirm('Delete this team? This cannot be undone.')) return;

    try {
      await deleteTeam(teamIdToDelete).unwrap();
      toast.success('Team deleted successfully.');
      navigate('/leagues');
    } catch (error) {
      console.error('Failed to delete team', error);
      toast.error('Failed to delete team');
    }
  };

  const handleSaveBenchSelection = async () => {
    if (!teamToDisplay || !configurableBenchWeekNumber) return;

    try {
      setIsSavingBench(true);
      await updateTeam({
        id: teamToDisplay._id,
        weeklyBenchSelections: {
          ...weeklyBenchSelections,
          [String(configurableBenchWeekNumber)]: selectedBenchAthletes,
        },
      }).unwrap();
      toast.success(`Bench selection saved for Week ${configurableBenchWeekNumber}.`);
    } catch (error) {
      console.error('Failed to save bench selection', error);
      toast.error(error?.data?.message || 'Failed to save bench selection');
    } finally {
      setIsSavingBench(false);
    }
  };

  const toggleBenchAthleteSelection = (athleteId) => {
    const normalizedAthleteId = String(athleteId || '');
    setSelectedBenchAthletes((currentSelections) => {
      if (currentSelections.includes(normalizedAthleteId)) {
        return currentSelections.filter((selectedId) => selectedId !== normalizedAthleteId);
      }

      return [...currentSelections, normalizedAthleteId];
    });
  };

  const isTeamOwner =
    String(getResolvedEntityId(teamToDisplay?.admin) || '') === String(userInfo?._id || '') ||
    String(getResolvedEntityId(athlete?.team) || '') === String(getResolvedEntityId(teamToDisplay) || '');

  if (isLoadingSpecificTeam || isLoadingDraftedAthletes || isLoadingLeagueTeams) {
    return (
      <div className="section-modern">
        <div className="container-modern">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-secondary">Loading team information...</p>
          </div>
        </div>
      </div>
    );
  }

  if (id && !teamToDisplay) {
    const errorMessage = specificTeamError?.data?.message || 'We could not load that team.';

    return (
      <div className="section-modern">
        <div className="container-modern">
          <Alert variant="warning" className="mb-0">
            <Alert.Heading>Team Unavailable</Alert.Heading>
            <p className="mb-3">{errorMessage}</p>
            <div className="d-flex gap-2 flex-wrap">
              <Button variant="primary" onClick={() => navigate('/team')}>
                Go To Your Team
              </Button>
              <Button variant="outline-primary" onClick={() => navigate('/leagues')}>
                View Leagues
              </Button>
            </div>
          </Alert>
        </div>
      </div>
    );
  }

  const rosterCount = draftedAthletes.length;
  const benchSlots = getBenchSlotsForRosterSize(rosterCount);
  const startersCount = Math.min(rosterCount, ACTIVE_SCORERS_PER_TEAM);
  const currentBenchAthleteIdSet = new Set(currentBenchAthleteIds);
  const fallbackRosterPoints = draftedAthletes.reduce(
    (sum, runner) => sum + (isRunnerBenched(runner, currentBenchAthleteIds) ? 0 : getRunnerSeasonPoints(runner)),
    0
  );
  const scoringSummary = teamToDisplay?.scoringSummary;
  const totalPoints = scoringSummary?.adjustedTotalPoints ?? fallbackRosterPoints;
  const baseRosterPoints = scoringSummary?.baseRosterPoints ?? fallbackRosterPoints;
  const ownerPointsMultiplier = scoringSummary?.ownerPointsMultiplier ?? 1;
  const ownerMultiplierCap = scoringSummary?.ownerMultiplierCap ?? 1;
  const ownerBonusPoints = scoringSummary?.ownerBonusPoints ?? 0;
  const totalSalary = draftedAthletes.reduce((sum, runner) => sum + (runner.salary || 0), 0);
  const salaryCap = teamToDisplay?.salaryCap || 1000000;
  const currentSalaryTotal = teamToDisplay?.currentSalaryTotal ?? totalSalary;
  const remainingCap = salaryCap - currentSalaryTotal;
  const leagueName = getLeagueName(teamToDisplay?.league || athlete?.league);
  const currentBenchAthletes = draftedAthletes.filter((runner) => currentBenchAthleteIdSet.has(String(runner._id)));
  const canManageBench = Boolean(isTeamOwner && benchSlots > 0 && configurableBenchWeekNumber);
  const displayTeamName = getDisplayTeamName(teamToDisplay?.name);
  const isBenchSelectionComplete = selectedBenchAthletes.length === benchSlots;

  return (
    <div className="section-modern">
      <div className="container-modern">
        <div className="fade-in">
          {athlete?.team && !athlete?.league && teamToDisplay && (
            <Alert variant="warning" className="mb-4">
              <Alert.Heading>Team Not in a League</Alert.Heading>
              <p className="mb-3">
                Your team <strong>{displayTeamName}</strong> exists, but it is not competing anywhere yet.
              </p>
              <Button variant="warning" onClick={() => navigate('/leagues')}>
                Join a League
              </Button>
            </Alert>
          )}

          {teamToDisplay ? (
            <>
              <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-5">
                <div>
                  <h1 className="text-primary mb-2">TEAM: {displayTeamName}</h1>
                  <p className="text-secondary mb-2">{teamToDisplay.description || 'No team description yet.'}</p>
                  <div className="d-flex gap-2 flex-wrap">
                    {currentDraft && <Badge bg="secondary">Draft: {currentDraft.status}</Badge>}
                  </div>
                </div>

                {isTeamOwner && (
                  <div className="d-flex gap-2">
                    <Button variant="outline-primary" size="sm" onClick={() => setShowEditModal(true)}>
                      <Icon icon={Edit3} size={16} />
                    </Button>
                    <Button variant="outline-danger" size="sm" onClick={() => handleDeleteTeam(teamToDisplay._id)}>
                      <Icon icon={Trash2} size={16} />
                    </Button>
                  </div>
                )}
              </div>

              <Row className="g-3 mb-4">
                <Col md={3}>
                  <Card className="modern-card h-100">
                    <Card.Body className="text-center">
                      <Icon icon={Users} size={22} className="mb-2" />
                      <h5 className="card-title">Weekly Lineup</h5>
                      <h3 className="text-primary">{startersCount}</h3>
                      <p className="text-secondary mb-0">
                        {rosterCount > 0 ? `${startersCount} active scorers and ${benchSlots} bench from ${rosterCount} total` : 'Draft your roster to set starters'}
                      </p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="modern-card h-100">
                    <Card.Body className="text-center">
                      <Icon icon={Trophy} size={22} className="mb-2" />
                      <h5 className="card-title">Team Points</h5>
                      <h3 className="text-primary">{totalPoints.toFixed(1)}</h3>
                      <p className="text-secondary mb-0">
                        {baseRosterPoints.toFixed(1)} roster pts + {ownerBonusPoints.toFixed(1)} owner pts
                      </p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="modern-card h-100">
                    <Card.Body className="text-center">
                      <Icon icon={Wallet} size={22} className="mb-2" />
                      <h5 className="card-title">Cap Used</h5>
                      <h3 className="text-primary">${currentSalaryTotal.toLocaleString()}</h3>
                      <p className="text-secondary mb-0">of ${salaryCap.toLocaleString()}</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="modern-card h-100">
                    <Card.Body className="text-center">
                      <Icon icon={UserMinus} size={22} className="mb-2" />
                      <h5 className="card-title">Bench Group</h5>
                      <h3 className="text-primary">{currentBenchAthletes.length > 0 ? currentBenchAthletes.length : benchSlots}</h3>
                      <p className="text-secondary mb-0">
                        {currentWeekNumber ? `Week ${currentWeekNumber} non-scoring athlete${currentBenchAthletes.length === 1 ? '' : 's'}` : 'Set once the season is active'}
                      </p>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Row className="g-4">
                <Col lg={8}>
                  <Card className="modern-card">
                    <div className="modern-card-header">
                      <h3 className="modern-card-title">Roster Contribution</h3>
                      <p className="modern-card-subtitle">Who is scoring for your team right now.</p>
                    </div>
                    <Card.Body>
                      {rosterCount > 0 ? (
                        <div className="table-responsive">
                          <Table hover>
                            <thead>
                              <tr>
                                <th>Athlete</th>
                                <th>Salary</th>
                                <th>Season Points</th>
                                <th>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {draftedAthletes
                                .slice()
                                .sort((a, b) => getRunnerSeasonPoints(b) - getRunnerSeasonPoints(a))
                                .map((runner) => (
                                  <tr key={runner._id}>
                                    <td>
                                      {runner?._id ? (
                                        <Button
                                          variant="link"
                                          className="p-0 align-baseline fw-semibold text-decoration-none"
                                          onClick={() => navigate(`/athlete?id=${runner._id}`)}
                                        >
                                          {getAthleteDisplayName(runner)}
                                        </Button>
                                      ) : (
                                        <strong>{getAthleteDisplayName(runner)}</strong>
                                      )}
                                    </td>
                                    <td>${(runner.salary || 0).toLocaleString()}</td>
                                    <td>{Number(getRunnerSeasonPoints(runner)).toFixed(1)}</td>
                                    <td>
                                      <Badge
                                        bg={
                                          currentBenchAthleteIdSet.has(String(runner._id))
                                            ? 'warning'
                                            : getRunnerSeasonPoints(runner) > 0
                                              ? 'success'
                                              : 'secondary'
                                        }
                                      >
                                        {currentBenchAthleteIdSet.has(String(runner._id))
                                          ? currentWeekNumber
                                            ? `Benched for Week ${currentWeekNumber}`
                                            : 'Bench Athlete'
                                          : getRunnerSeasonPoints(runner) > 0
                                            ? 'Starter'
                                            : 'Waiting on sync'}
                                      </Badge>
                                    </td>
                                  </tr>
                                ))}
                            </tbody>
                          </Table>
                        </div>
                      ) : (
                        <div className="text-center py-5">
                          <h5 className="text-secondary">No roster yet</h5>
                          <p className="text-secondary mb-3">
                            Draft athletes to turn this into a competitive team.
                          </p>
                          {currentDraft?.status === 'active' && (
                            <Button variant="primary" onClick={() => navigate(buildDraftRoomPath(currentDraft._id, { chooseView: true }))}>
                              Join Active Draft
                            </Button>
                          )}
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>

                <Col lg={4}>
                  <Card className="modern-card mb-4">
                    <div className="modern-card-header">
                      <h3 className="modern-card-title">Weekly Bench</h3>
                      <p className="modern-card-subtitle">Set the non-scoring athletes before the target week begins.</p>
                    </div>
                    <Card.Body>
                      {currentWeekNumber || configurableBenchWeekNumber ? (
                        <>
                          <div className="mb-3">
                            <strong className="text-secondary d-block">Current Scoring Week</strong>
                            <span>{currentWeekNumber ? `Week ${currentWeekNumber}` : 'Season has not started yet'}</span>
                          </div>
                          <div className="mb-3">
                            <strong className="text-secondary d-block">Current Bench</strong>
                            <span>
                              {currentBenchAthletes.length > 0
                                ? currentBenchAthletes.map((runner) => getAthleteDisplayName(runner)).join(', ')
                                : benchSlots > 0
                                  ? 'No bench athletes selected yet'
                                  : 'All drafted athletes are active scorers'}
                            </span>
                          </div>
                          {benchSlots <= 0 ? (
                            <p className="text-secondary mb-0">
                              This roster size does not require a bench. All drafted athletes score each week.
                            </p>
                          ) : canManageBench ? (
                            <>
                              <div className="form-group-modern">
                                <label className="form-label-modern">Week {configurableBenchWeekNumber} bench athletes</label>
                                <div className="d-grid gap-2">
                                  {draftedAthletes.map((runner) => {
                                    const isSelected = selectedBenchAthletes.includes(String(runner._id));
                                    const selectionLimitReached = !isSelected && selectedBenchAthletes.length >= benchSlots;

                                    return (
                                      <Form.Check
                                        key={runner._id}
                                        type="checkbox"
                                        id={`bench-${runner._id}`}
                                        label={`${getAthleteDisplayName(runner)} (${Number(getRunnerSeasonPoints(runner)).toFixed(1)} pts)`}
                                        checked={isSelected}
                                        disabled={selectionLimitReached}
                                        onChange={() => toggleBenchAthleteSelection(runner._id)}
                                      />
                                    );
                                  })}
                                </div>
                              </div>
                              <div className="team-bench-note">
                                Select exactly {benchSlots} bench athlete{benchSlots === 1 ? '' : 's'} for Week {configurableBenchWeekNumber}. Lineups lock when that week begins.
                              </div>
                              <Button
                                variant="primary"
                                className="w-100"
                                onClick={handleSaveBenchSelection}
                                disabled={!isBenchSelectionComplete || isSavingBench}
                              >
                                {isSavingBench ? 'Saving Bench...' : `Save Week ${configurableBenchWeekNumber} Bench`}
                              </Button>
                            </>
                          ) : (
                            <p className="text-secondary mb-0">
                              {isTeamOwner
                                ? 'The next bench selection unlocks once a future scoring week is available.'
                                : 'Only the team manager can change the weekly bench.'}
                            </p>
                          )}
                        </>
                      ) : (
                        <p className="text-secondary mb-0">
                          Bench selections unlock once the league has an active season schedule.
                        </p>
                      )}
                    </Card.Body>
                  </Card>

                  <Card className="modern-card mb-4">
                    <div className="modern-card-header">
                      <h3 className="modern-card-title">Team Actions</h3>
                      <p className="modern-card-subtitle">Go where decisions happen.</p>
                    </div>
                    <Card.Body className="d-grid gap-2">
                      <Button variant="primary" onClick={() => navigate('/leagues')}>
                        View League Standings
                      </Button>
                      {currentDraft && (
                        <Button
                          variant="outline-primary"
                          onClick={() => navigate(buildDraftRoomPath(currentDraft._id, { chooseView: true }))}
                          disabled={currentDraft.status === 'completed'}
                        >
                          {currentDraft.status === 'active' ? 'Enter Draft' : currentDraft.status === 'scheduled' ? 'View Draft' : 'Draft Complete'}
                        </Button>
                      )}
                      <Button variant="outline-primary" onClick={() => navigate('/profile')}>
                        Open Profile
                      </Button>
                    </Card.Body>
                  </Card>

                  <Card className="modern-card">
                    <div className="modern-card-header">
                      <h3 className="modern-card-title">Competition Context</h3>
                      <p className="modern-card-subtitle">The details that affect team management.</p>
                    </div>
                    <Card.Body>
                      <div className="mb-3">
                        <strong className="text-secondary d-block">League</strong>
                        <span>{leagueName}</span>
                      </div>
                      <div className="mb-3">
                        <strong className="text-secondary d-block">Draft Status</strong>
                        <span>{currentDraft ? currentDraft.status : 'No draft scheduled'}</span>
                      </div>
                      <div className="mb-3">
                        <strong className="text-secondary d-block">Owner Multiplier</strong>
                        <span>
                          {ownerPointsMultiplier.toFixed(3)}x
                          {scoringSummary ? ` of ${ownerMultiplierCap.toFixed(3)}x ceiling (${scoringSummary.ownerName}: ${scoringSummary.ownerMiles.toFixed(1)} mi, rank #${scoringSummary.ownerSalaryRank || 'n/a'})` : ''}
                        </span>
                      </div>
                      <div className="mb-3">
                        <strong className="text-secondary d-block">Rank-Based Ceiling</strong>
                        <span>{ownerMultiplierCap.toFixed(3)}x maximum owner multiplier for this team</span>
                      </div>
                      <div className="mb-3">
                        <strong className="text-secondary d-block">Owner Bonus</strong>
                        <span>{ownerBonusPoints >= 0 ? '+' : ''}{ownerBonusPoints.toFixed(1)} points</span>
                      </div>
                      <div className="mb-3">
                        <strong className="text-secondary d-block">Season Week</strong>
                        <span>{currentWeekNumber ? `Week ${currentWeekNumber}` : 'Season not active'}</span>
                      </div>
                      <div className="mb-3">
                        <strong className="text-secondary d-block">Active Scorers</strong>
                        <span>{startersCount} scoring athlete{startersCount === 1 ? '' : 's'} each week</span>
                      </div>
                      <div>
                        <strong className="text-secondary d-block">Cap Remaining</strong>
                        <span>${remainingCap.toLocaleString()}</span>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </>
          ) : (
            <div className="text-center py-5">
              <h2 className="text-primary mb-3">Create Your Team</h2>
              <p className="text-secondary mb-5">Start with a team so you can join a league, draft athletes, and compete.</p>

              <Row className="justify-content-center">
                <Col md={8} lg={6}>
                  <Card className="modern-card">
                    <Card.Body>
                      <Form onSubmit={handleCreateTeam}>
                        <div className="form-group-modern">
                          <label className="form-label-modern">Team Name *</label>
                          <input
                            type="text"
                            className="form-input-modern"
                            placeholder="Enter your team name"
                            value={teamName}
                            onChange={(e) => setTeamName(e.target.value)}
                            required
                          />
                        </div>
                        <div className="form-group-modern">
                          <label className="form-label-modern">Description</label>
                          <textarea
                            className="form-input-modern"
                            rows="3"
                            placeholder="Optional team description"
                            value={teamDescription}
                            onChange={(e) => setTeamDescription(e.target.value)}
                          />
                        </div>
                        <div className="form-group-modern">
                          <label className="form-label-modern">League *</label>
                          <select
                            className="form-input-modern"
                            value={selectedLeague}
                            onChange={(e) => setSelectedLeague(e.target.value)}
                            disabled={isCreateTeamLockedToLeague}
                            required
                          >
                            <option value="">Select a League</option>
                            {leagues?.map((league) => (
                              <option key={league._id} value={league._id}>
                                {league.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="d-grid gap-2">
                          <Button type="submit" variant="primary">
                            Create Team
                          </Button>
                          <Button variant="outline-primary" onClick={() => navigate(requestedLeagueId ? `/leagues/${requestedLeagueId}` : '/leagues')}>
                            Browse Leagues
                          </Button>
                        </div>
                      </Form>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </div>
          )}

          <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered>
            <Modal.Header closeButton>
              <Modal.Title>Edit Team</Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleUpdateTeam}>
              <Modal.Body>
                <div className="form-group-modern">
                  <label className="form-label-modern">Team Name</label>
                  <input
                    type="text"
                    className="form-input-modern"
                    value={teamName}
                    onChange={(e) => setTeamName(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group-modern">
                  <label className="form-label-modern">Description</label>
                  <textarea
                    className="form-input-modern"
                    rows="3"
                    value={teamDescription}
                    onChange={(e) => setTeamDescription(e.target.value)}
                  />
                </div>
                <div className="form-group-modern">
                  <label className="form-label-modern">League</label>
                  <select
                    className="form-input-modern"
                    value={selectedLeague}
                    onChange={(e) => setSelectedLeague(e.target.value)}
                  >
                    <option value="">Select a League</option>
                    {leagues?.map((league) => (
                      <option key={league._id} value={league._id}>
                        {league.name}
                      </option>
                    ))}
                  </select>
                </div>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowEditModal(false)}>
                  Cancel
                </Button>
                <Button variant="primary" type="submit">
                  Save Changes
                </Button>
              </Modal.Footer>
            </Form>
          </Modal>
        </div>
      </div>
    </div>
  );
};

export default TeamScreen;
